import { TextArea } from '@react-spectrum/s2';
import EditIcon from '@react-spectrum/s2/icons/Edit';
import { iconStyle } from '@react-spectrum/s2/style' with { type: 'macro' };
import { useState } from 'react';

import { cn } from '@/utils/cn';

export const MutedLabel = ({ children }: { children: React.ReactNode }) => (
	<span
		style={{
			fontSize: '0.5625rem',
			fontWeight: 700,
			letterSpacing: '0.07em',
			textTransform: 'uppercase' as const,
			color: 'var(--text-muted)',
		}}
	>
		{children}
	</span>
);

interface CollapsiblePromptProps {
	value: string;
	isReadOnly?: boolean;
	onChange: (value: string) => void;
	onCommit: (value: string) => void;
}

export const CollapsiblePrompt = ({
	value,
	isReadOnly,
	onChange,
	onCommit,
}: CollapsiblePromptProps) => {
	const [expanded, setExpanded] = useState(false);
	const preview = value || '(No prompt set)';

	return (
		<div className="w-full max-w-full min-w-0 overflow-hidden">
			<div
				className={cn(
					`flex w-full max-w-full min-w-0 items-center gap-1.5 overflow-hidden
					bg-[#FAFAFA] py-[5px] pr-1.5 pl-2.5`,
					'transition-[border-color,border-radius] duration-150',
					expanded
						? 'rounded-t-lg border border-b-0 border-[#C8C8C8]'
						: 'rounded-lg border border-[#E5E5E5]',
				)}
			>
				<div
					className={cn(
						'w-0 min-w-0 flex-1 basis-0 truncate overflow-hidden text-xs',
						expanded && 'invisible',
						value ? 'text-(--text-secondary)' : 'text-(--text-muted) italic',
					)}
				>
					{preview}
				</div>
				<div className="flex shrink-0 items-center gap-1.5">
					<button
						type="button"
						onClick={() => {
							if (expanded) {
								setExpanded(false);
							} else {
								setExpanded(true);
							}
						}}
						className="inline-flex shrink-0 cursor-pointer items-center gap-1
							rounded border-0 bg-transparent px-1.5 py-[3px] text-xs
							font-medium text-(--text-secondary)"
						aria-label={
							expanded
								? 'Collapse prompt'
								: isReadOnly
									? 'View prompt'
									: 'Edit prompt'
						}
					>
						{!expanded && !isReadOnly && (
							<>
								<EditIcon styles={iconStyle({ size: 'XS' })} />
								<span>Edit</span>
							</>
						)}
						{!expanded && isReadOnly && <span>View</span>}
						{expanded && !isReadOnly && <span>Done</span>}
						{expanded && isReadOnly && <span>Close</span>}
					</button>
				</div>
			</div>

			{expanded && (
				<div
					className="max-w-full overflow-hidden rounded-b-lg border border-t-0
						border-[#C8C8C8] bg-white p-2 pb-1.5"
				>
					{isReadOnly ? (
						<p
							className={cn(
								'm-0 text-xs leading-normal wrap-break-word whitespace-pre-wrap',
								value
									? 'text-(--text-secondary)'
									: 'text-(--text-muted) italic',
							)}
						>
							{value || '(No prompt set)'}
						</p>
					) : (
						<TextArea
							aria-label="Prompt"
							value={value}
							onChange={(nextValue) => onChange(nextValue)}
							onBlur={() => onCommit(value)}
							UNSAFE_style={{ fontSize: '0.75rem' }}
						/>
					)}
				</div>
			)}
		</div>
	);
};
